
/// <reference path="../base/BaseTableViewController.ts" />

class LicensesListViewController extends BaseTableViewController
{    
    static newInstance() : LicensesListViewController {
        let vc = new LicensesListViewController("licenses-list-view");
        vc.initWithResource("layout/licenses/LicensesListView.html");
        return vc;
    }

    private addButton:MUIButton = null;

    viewDidLoad(){
        super.viewDidLoad();

        this.addButton = MUIOutlet(this, "add-btn", "MUIButton");
        this.addButton.setAction(this, this.addBusinessLicense);

        this.searchTextField = MUIOutlet(this, "search-bar", "MUITextField");
        this.setupSearchTextField();

        this.tableView = MUIOutlet(this,"table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
        
        this.tableView.reloadData();
    }

    viewWillAppear(animated?: boolean): void {
        super.viewWillAppear( animated );

        MIONotificationCenter.defaultCenter().addObserver( this, "BusinessLicenseDidDelete", function(this:LicensesListViewController, object:any, userInfo:any){
            if ( this.selectedItem != object ) return;
            this.showNoItemSelected();
            this.selectedItem = null;
        });
    }

    viewDidDisappear(animated?: boolean): void {
        super.viewDidDisappear( animated );

        MIONotificationCenter.defaultCenter().removeObserver( this, "BusinessLicenseDidDelete" );
    }
    
    cellAtIndexPath(tableview:UITableView, indexPath:MIOIndexPath){        
        let item = this.fetchedResultsController.objectAtIndexPath(indexPath);
        
        let cell = tableview.dequeueReusableCellWithIdentifier("LicenseCell") as LicenseCell; 
        cell.item = item;   
        cell.selected = (item == this.selectedItem);
        
        return cell;
    }

    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        this.selectedItem = this.fetchedResultsController.objectAtIndexPath(indexPath);
        this.showSelectedItem();
    }

    private showSelectedItem(){
        let vc = LicensesDetailViewController.newInstance();
        vc.item = this.selectedItem;
        this.splitViewController.showDetailViewController(vc);        
    }

    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate;

        let pf = null;
        if (this.searchString != null) {
            pf = "name CONTAINS '" + this.searchString + "'";            
        }        

        let sds = [MIOSortDescriptor.sortDescriptorWithKey("name", true)];
        let fetchRequest = DBHelper.listFetchRequestWithEntityName( "BusinessLicense", sds, pf );            
        fetchRequest.relationshipKeyPathsForPrefetching = ["client"];
        fetchRequest.fetchLimit = 100;

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;

        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    private showNoItemSelected(){
        let vc = AppHelper.sharedInstance().emptyViewControllerForIndentifier("License", NoItemSelectedViewControllerType.License);        
        this.splitViewController.showDetailViewController(vc);
    }

    private addBusinessLicense() {
        AppHelper.sharedInstance().showSelectClientViewControllerFromView(this.addButton, null, true, this, function(this:LicensesListViewController, controller:SelectViewController, value:Client){
            this.showNameAlert(value);
        }, this, function(){
            this.showAddClientViewController();
        });
    }

    private createLicence(client:Client, name:string) : BusinessLicense {
        // TODO: Check if alreaady exists
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let lic = MIOEntityDescription.insertNewObjectForEntityForName( "BusinessLicense", ad.managedObjectContext) as BusinessLicense;

        lic.client = client;
        lic.active = true;
        lic.name = name;

        this.selectedItem = lic;

        DBHelper.saveMainContext();   
        
        this.showSelectedItem();
        return lic;
    }

    // The licence is saved first so the HQ route can find it, then auth creates the
    // venue for it and links it. Until the answer arrives the row shows unlinked (yellow),
    // which is also what it stays if the venue creation fails.
    private createLicenceWithVenue(client:Client, name:string, venue:any){
        let lic = this.createLicence(client, name);
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        DBHelper.saveMainContextWithCompletion(this, function(this:LicensesListViewController){
            ad.webService.createBusinessLicenseVenue(lic, venue, (data:any, error:string) => {
                if (error != null) {
                    AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("THE_LICENCE_WAS_CREATED_BUT_NOT_ITS_VENUE", "THE LICENCE WAS CREATED BUT NOT ITS VENUE") + ": " + error);
                    return;
                }
                // The server already wrote the link; mirror it so the list turns from
                // yellow to normal without a refetch.
                lic.authSchema = data["venueID"];
                lic.dbSchema = data["venueID"];
                this.tableView.reloadData();
            });
        });
    }

    private showAddClientViewController(){
        
        let vc = new AddNewClientViewController('add-new-client-view');            
        vc.initWithResource('layout/clients/AddNewClientView.html');
        // vc.delegate = this;
        
        this.presentViewController(vc, true);
    }        

    // Name plus what auth needs to open the venue (country, postal code, language,
    // timezone). "CREATE WITH VENUE" makes the licence and the venue together, already
    // linked; "LICENCE ONLY" is the old behaviour for a customer whose venue does not
    // exist yet or already signed up on its own (link it from the licence settings).
    private showNameAlert(client:Client) {
        let avc = new MUIAlertViewController();
        avc.initWithTitle(MIOLocalizeString("NEW_BUSINESS", "NEW BUSINESS"), MIOLocalizeString("NAME_AND_VENUE_DETAILS", "NAME AND VENUE DETAILS"), MUIAlertViewStyle.Default);

        let placeholders = [ ["NAME", "NAME", client.name ?? ""], ["COUNTRY_ISO_CODE", "COUNTRY ISO CODE", "ES"], ["POSTAL_CODE", "POSTAL CODE", ""], ["LANGUAGE_ISO_CODE", "LANGUAGE ISO CODE", "es"], ["TIMEZONE", "TIMEZONE", "Europe/Madrid"] ];
        for (let p of placeholders) {
            avc.addTextFieldWithConfigurationHandler(this, function(control:MUITextField){
                control.placeholderText = MIOLocalizeString(p[0], p[1]);
                control.text = p[2];
            });
        }

        let fields = function() : [string, any] {
            let name = avc.textFields[0].text.trim();
            let venue = { "CountryISOCode": avc.textFields[1].text.trim().toUpperCase()
                        , "PostalCode": avc.textFields[2].text.trim()
                        , "LanguageISOCode": avc.textFields[3].text.trim().toLowerCase()
                        , "Timezone": avc.textFields[4].text.trim() };
            return [name, venue];
        }

        avc.addAction( MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CREATE_WITH_VENUE", "CREATE WITH VENUE"), MUIAlertActionStyle.Default, this, function(this:LicensesListViewController){
            let [name, venue] = fields();
            if (name.length == 0 || venue["CountryISOCode"].length == 0 || venue["LanguageISOCode"].length == 0 || venue["Timezone"].length == 0) {
                AppHelper.showErrorMessage(this, MIOLocalizeString("ERROR", "ERROR"), MIOLocalizeString("NAME_COUNTRY_LANGUAGE_AND_TIMEZONE_ARE_REQUIRED", "NAME, COUNTRY, LANGUAGE AND TIMEZONE ARE REQUIRED"));
                return;
            }
            this.createLicenceWithVenue(client, name, venue);
        }));

        avc.addAction( MUIAlertAction.alertActionWithTitle(MIOLocalizeString("LICENCE_ONLY", "LICENCE ONLY"), MUIAlertActionStyle.Default, this, function(this:LicensesListViewController){
            let [name, ] = fields();
            if (name.length == 0) return;
            this.createLicence(client, name);
        }));

        avc.addAction( MUIAlertAction.alertActionWithTitle(MIOLocalizeString("CANCEL", "CANCEL"), MUIAlertActionStyle.Cancel, null, null) ); 
        
        this.presentViewController(avc, true);
    }
    
}